(() => {
  const REGION_STORAGE_KEY = 'aquemoto-service-region';
  const REGION_LABELS = {
    mainland_china: 'China mainland',
    north_america: 'North America',
    peru: 'Perú',
    south_america: 'South America',
  };

  const getStoredRegion = () => {
    try {
      return window.localStorage.getItem(REGION_STORAGE_KEY) || '';
    } catch (_error) {
      return '';
    }
  };

  const storeRegion = (region) => {
    try {
      window.localStorage.setItem(REGION_STORAGE_KEY, region);
    } catch (_error) {
      // Storage can be unavailable in privacy modes. The current page still updates.
    }
  };

  const normalizeRegion = (value) => {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    const aliases = {
      cn: 'mainland_china',
      china: 'mainland_china',
      china_mainland: 'mainland_china',
      mainland_china: 'mainland_china',
      na: 'north_america',
      northamerica: 'north_america',
      pe: 'peru',
      perú: 'peru',
      sa: 'south_america',
      other_south_america: 'south_america',
    };
    const resolved = aliases[normalized] || normalized;
    return REGION_LABELS[resolved] ? resolved : '';
  };

  const updateConsultationLinks = (region) => {
    if (!region) return;
    document.querySelectorAll('a[data-aq-consultation-link]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('region', region);
        link.href = url.toString();
      } catch (_error) {
        // Leave malformed or non-http links untouched.
      }
    });
  };

  const applyServiceRegion = (value, shouldStore = true) => {
    const region = normalizeRegion(value) || 'north_america';
    const label = REGION_LABELS[region];
    document.documentElement.dataset.aquemotoServiceRegion = region;
    document.querySelectorAll('[data-aq-service-current]').forEach((node) => {
      node.textContent = node.dataset.aqServiceShort === 'true'
        ? ({ mainland_china: 'CN', north_america: 'NA', peru: 'PE', south_america: 'SA' }[region] || 'NA')
        : label;
    });
    document.querySelectorAll('[data-aq-service-region]').forEach((button) => {
      const isCurrent = normalizeRegion(button.dataset.aqServiceRegion) === region;
      button.classList.toggle('is-current', isCurrent);
      button.setAttribute('aria-pressed', String(isCurrent));
    });
    document.querySelectorAll('select[data-aq-service-select]').forEach((select) => {
      if ([...select.options].some((option) => option.value === region)) select.value = region;
    });
    document.querySelectorAll('input[data-aq-service-input]').forEach((input) => {
      input.value = region;
      input.defaultValue = region;
    });
    updateConsultationLinks(region);
    if (shouldStore) storeRegion(region);
    document.dispatchEvent(new CustomEvent('aquemoto:service-region-change', {
      detail: { region, label },
    }));
  };

  const initServiceRegions = () => {
    const queryRegion = normalizeRegion(new URLSearchParams(window.location.search).get('region'));
    const selectedRegion = queryRegion || normalizeRegion(getStoredRegion()) || 'north_america';
    applyServiceRegion(selectedRegion, Boolean(queryRegion));

    if (document.documentElement.dataset.aqServiceListeners === 'true') return;
    document.documentElement.dataset.aqServiceListeners = 'true';

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-aq-service-region]');
      if (!button) return;
      event.preventDefault();
      applyServiceRegion(button.dataset.aqServiceRegion);
      if (button.closest('[data-aq-keep-open="true"]')) return;
      const details = button.closest('details');
      if (details) details.removeAttribute('open');
    });

    document.addEventListener('change', (event) => {
      const select = event.target.closest('select[data-aq-service-select]');
      if (select) applyServiceRegion(select.value);
    });
  };

  const initHeaderDisclosures = () => {
    const disclosures = [...document.querySelectorAll('[data-aq-disclosure]')];
    disclosures.forEach((details) => {
      if (details.dataset.aqDisclosureReady === 'true') return;
      details.dataset.aqDisclosureReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('[data-aq-disclosure]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });

    if (document.documentElement.dataset.aqDisclosureListeners === 'true') return;
    document.documentElement.dataset.aqDisclosureListeners = 'true';

    document.addEventListener('click', (event) => {
      document.querySelectorAll('[data-aq-disclosure]').forEach((details) => {
        if (details.open && !details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-aq-disclosure]').forEach((details) => {
        if (!details.open) return;
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      });
    });
  };

  const initMobileNavigation = () => {
    document.querySelectorAll('[data-aq-mobile-drawer]').forEach((drawer) => {
      if (drawer.dataset.aqReady === 'true') return;
      drawer.dataset.aqReady = 'true';
      const summary = drawer.querySelector(':scope > summary');
      const panel = drawer.querySelector('[data-aq-mobile-panel]');
      const focusableSelector = 'a[href],button:not([disabled]),select:not([disabled]),input:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
      const desktopViewport = window.matchMedia('(min-width: 990px)');

      const close = (restoreFocus = true) => {
        drawer.removeAttribute('open');
        document.documentElement.classList.remove('aq-nav-open');
        summary?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) summary?.focus();
      };

      drawer.querySelectorAll('[data-aq-drawer-close]').forEach((control) => {
        control.addEventListener('click', () => close(control.tagName === 'BUTTON'));
      });
      drawer.addEventListener('toggle', () => {
        document.documentElement.classList.toggle('aq-nav-open', drawer.open);
        summary?.setAttribute('aria-expanded', String(drawer.open));
        if (drawer.open) window.requestAnimationFrame(() => panel?.querySelector('[data-aq-drawer-close]')?.focus());
      });
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          return;
        }
        if (event.key !== 'Tab' || !drawer.open || !panel) return;
        const focusable = [...panel.querySelectorAll(focusableSelector)].filter((node) => node.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      summary?.setAttribute('aria-expanded', String(drawer.open));

      const closeAtDesktop = (event) => {
        if (event.matches && drawer.open) close(false);
      };
      desktopViewport.addEventListener('change', closeAtDesktop);
      drawer.aqCleanup = () => desktopViewport.removeEventListener('change', closeAtDesktop);
    });

    document.documentElement.classList.toggle('aq-nav-open', Boolean(document.querySelector('[data-aq-mobile-drawer][open]')));

    if (document.documentElement.dataset.aqMobileUnloadListener === 'true') return;
    document.documentElement.dataset.aqMobileUnloadListener = 'true';
    document.addEventListener('shopify:section:unload', (event) => {
      const drawer = event.target?.querySelector?.('[data-aq-mobile-drawer]');
      if (!drawer) return;
      drawer.aqCleanup?.();
      drawer.removeAttribute('open');
      document.documentElement.classList.toggle('aq-nav-open', Boolean(document.querySelector('[data-aq-mobile-drawer][open]')));
    });
  };

  const recommendationFor = (motion, profile) => {
    if (motion === 'flight') return 'hydrofoil';
    if (profile === 'carbon') return 'carbon-max';
    return 'jetboard';
  };

  const initRideFinder = () => {
    document.querySelectorAll('[data-aq-ride-finder]').forEach((finder) => {
      if (finder.dataset.aqReady === 'true') return;
      finder.dataset.aqReady = 'true';
      const form = finder.querySelector('[data-aq-finder-form]');
      const result = finder.querySelector('[data-aq-finder-result]');
      const title = finder.querySelector('[data-aq-finder-title]');
      const copy = finder.querySelector('[data-aq-finder-copy]');
      const productLink = finder.querySelector('[data-aq-finder-product-link]');
      const consultationLink = finder.querySelector('[data-aq-finder-consultation-link]');
      const media = [...finder.querySelectorAll('[data-aq-finder-media]')];
      if (!form || !result) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const values = new FormData(form);
        const recommendation = recommendationFor(values.get('motion'), values.get('profile'));
        const option = finder.querySelector(`[data-aq-finder-option="${recommendation}"]`);
        if (!option) return;

        title.textContent = option.dataset.title || '';
        copy.textContent = option.dataset.copy || '';
        productLink.href = option.dataset.url || '#';
        media.forEach((item) => { item.hidden = item.dataset.aqFinderMedia !== recommendation; });

        const region = normalizeRegion(values.get('region')) || normalizeRegion(getStoredRegion()) || 'north_america';
        applyServiceRegion(region);
        try {
          const url = new URL(option.dataset.consultationUrl || consultationLink.href, window.location.href);
          url.searchParams.set('watercraft', recommendation);
          url.searchParams.set('region', region);
          url.searchParams.set('experience', String(values.get('experience') || ''));
          url.searchParams.set('use', String(values.get('ownership') || ''));
          consultationLink.href = url.toString();
        } catch (_error) {
          consultationLink.href = option.dataset.consultationUrl || consultationLink.href;
        }
        result.hidden = false;
        result.dataset.empty = 'false';
        result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
        result.focus({ preventScroll: true });
      });
    });
  };

  const updateHeaderState = () => {
    const isScrolled = window.scrollY > 18;
    document.querySelectorAll('[data-aq-header-shell]').forEach((shell) => {
      shell.classList.toggle('is-scrolled', isScrolled);
    });
  };

  const initHeaderState = () => {
    updateHeaderState();
    if (document.documentElement.dataset.aqHeaderStateReady === 'true') return;
    document.documentElement.dataset.aqHeaderStateReady = 'true';
    window.addEventListener('scroll', updateHeaderState, { passive: true });
  };

  const initModelTheatre = () => {
    document.querySelectorAll('[data-aq-model-theatre]').forEach((theatre) => {
      if (theatre.dataset.aqReady === 'true') return;
      theatre.dataset.aqReady = 'true';
      const tabs = [...theatre.querySelectorAll('[data-aq-model-tab]')];
      const panels = [...theatre.querySelectorAll('[data-aq-model-panel]')];
      if (!tabs.length || !panels.length) return;

      const activate = (tab, focus = false) => {
        const value = tab.dataset.aqModelTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.aqModelPanel === value;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
          else nextIndex = (index + 1) % tabs.length;
          activate(tabs[nextIndex], true);
        });
      });
    });
  };

  const initOwnershipSystem = () => {
    document.querySelectorAll('[data-aq-ownership]').forEach((system) => {
      if (system.dataset.aqReady === 'true') return;
      system.dataset.aqReady = 'true';
      const tabs = [...system.querySelectorAll('[data-aq-ownership-tab]')];
      const panels = [...system.querySelectorAll('[data-aq-ownership-panel]')];

      const activate = (tab, focus = false) => {
        const value = tab.dataset.aqOwnershipTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.aqOwnershipPanel === value;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          else nextIndex = (index + 1) % tabs.length;
          activate(tabs[nextIndex], true);
        });
      });

      const mobileDetails = [...system.querySelectorAll('.aq-ownership-standard__mobile details')];
      mobileDetails.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          mobileDetails.forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
        });
      });
    });
  };

  const init = () => {
    initServiceRegions();
    initHeaderDisclosures();
    initMobileNavigation();
    initRideFinder();
    initHeaderState();
    initModelTheatre();
    initOwnershipSystem();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
